import animate from '@play-co/timestep-core/lib/animate';
import View from '@play-co/timestep-core/lib/ui/View';
import uiConfig from 'src/lib/ui/config';

import Application from 'src/Application';
import Header from 'src/game/components/Header';

import StateObserver from 'src/StateObserver';
import { createPersistentEmitter } from 'src/lib/Emitter';
import { SceneID, setTransitionPhase } from 'src/state/ui';

import { animDuration, getRootView, waitForItPromise } from 'src/lib/utils';
import { assertNever } from 'src/replicant/utils';
import LivingRoomScene from '../scenes/LivingRoomScene';
import Footer from '../components/Footer';
import DiningRoomScene from '../scenes/DiningRoomScene';
import ToiletRoomScene from '../scenes/ToiletRoomScene';
import BedroomScene from '../scenes/BedroomScene';
import BathroomScene from '../scenes/BathroomScene';
import MinigameScene from '../scenes/MinigameScene';
import FriendsLivingRoomScene from '../scenes/FriendsLivingRoomScene';
import { FloatAnimator } from 'src/lib/Animator';
import DebutScene from '../scenes/DebutScene';

export type Transition = 'fade' | 'slide_forward' | 'slide_back';
export interface TransitioningScene {
  showScene(root: View): void;

  hideScene(root: View): void;

  transition(): Transition;
}

export const SLIDE_TRANSITION_DURATION = animDuration * 3;

export default class SceneManager {
  private app: Application;
  private previousSceneID: SceneID = null;
  public header: Header;
  public footer: Footer;
  public scenes: {
    livingRoom: LivingRoomScene;
    diningRoom: DiningRoomScene;
    toiletRoom: ToiletRoomScene;
    bedroom: BedroomScene;
    friendsLivingRoom: FriendsLivingRoomScene;
    bathroom: BathroomScene;
    minigame: MinigameScene;
    debut: DebutScene;
  };

  constructor(opts: { app: Application }) {
    this.app = opts.app;

    // create hud
    this.header = new Header({ superview: this.app.getRootView() });
    this.footer = new Footer({ superview: this.app.getRootView() });

    // create scenes
    this.scenes = {
      livingRoom: new LivingRoomScene(),
      friendsLivingRoom: new FriendsLivingRoomScene(),
      diningRoom: new DiningRoomScene(),
      toiletRoom: new ToiletRoomScene(),
      bedroom: new BedroomScene(),
      bathroom: new BathroomScene(),
      minigame: new MinigameScene(),
      debut: new DebutScene(),
    };

    // listen to scene change
    createPersistentEmitter(
      ({ ui }) => ui.transition.nextScene,
    ).addListener((name) => this.gotoScene(name));
  }

  // ===================================================================
  // Scene Navigation
  // ===================================================================

  private gotoScene(name: SceneID) {
    // part of scene reload: don't do anything
    if (!name) return;

    if (!this.scenes[name]) {
      this.createScene(name);
    }

    const rootView = this.app.getRootView();

    const scene = this.scenes[name];
    const sceneView = scene.getView();
    const fromScene = rootView.stack[rootView.stack.length - 1];

    // Detect and remember previous scene
    const previousScene = this.scenes[this.previousSceneID];
    this.previousSceneID = name;

    // Call hide callback on previous scene
    previousScene?.hideScene(rootView);

    // swipe transition type:
    const transition = previousScene ? scene.transition() : 'fade';

    void scene.onBeforeTransition();

    // Select scene transition animation
    switch (transition) {
      case 'fade':
        this.fadeAnimation(fromScene, sceneView, scene);
        break;
      case 'slide_back':
      case 'slide_forward':
        this.slideAnimation(fromScene, sceneView, scene);
        break;
      default:
        assertNever(transition);
    }
  }

  private fadeAnimation(
    fromScene: View,
    sceneView: View,
    scene: TransitioningScene,
  ) {
    // transition out
    this.fadeOut(fromScene, () => {
      // this has become a computed based state, checkable with isSceneLeft()
      // StateObserver.dispatch(setTransitionPhase('left'));

      // update stack
      const rootView = this.app.getRootView();
      rootView.pop(true);
      rootView.push(sceneView, true);

      // The new sceneView intial setup must happen BEFORE awaiting for loading
      // or otherwise we'll be seeing the screen while loading,
      // in other words, flickering.
      sceneView.updateOpts({
        visible: false,
        opacity: 0,
      });

      // initialize new scene (before new scene has faded in)
      StateObserver.dispatch(setTransitionPhase('entering'));
      this.fadeIn(sceneView, animDuration, () => {
        scene.showScene(rootView);
        StateObserver.dispatch(setTransitionPhase('entered'));
      });
    });
  }

  private slideAnimation(
    fromScene: View,
    targetScene: View,
    scene: TransitioningScene,
  ) {
    // update stack
    const rootView = this.app.getRootView();

    // initialize new scene (before new scene has faded in)
    StateObserver.dispatch(setTransitionPhase('entering'));

    targetScene.updateOpts({ x: uiConfig.width });

    const dir = scene.transition() === 'slide_back' ? -1 : 1;

    // Animate
    this.slideOut(fromScene, SLIDE_TRANSITION_DURATION, -dir);

    targetScene.updateOpts({ zIndex: -999 });

    rootView.addSubview(targetScene);

    this.slideIn(targetScene, SLIDE_TRANSITION_DURATION, dir);

    void waitForItPromise(SLIDE_TRANSITION_DURATION).then(() => {
      rootView.pop(true);

      const { width, height } = targetScene.style;
      rootView.push(targetScene, true);
      // Reset scene dimensions because StackView resizes pushed views to match itself
      targetScene.updateOpts({ width, height });

      scene.showScene(rootView);
      StateObserver.dispatch(setTransitionPhase('entered'));
    });
  }

  private createScene(name: SceneID) {
    switch (name) {
      case 'debut':
      case 'livingRoom':
      case 'friendsLivingRoom':
      case 'diningRoom':
      case 'toiletRoom':
      case 'bedroom':
      case 'bathroom':
      case 'minigame': {
        throw new Error(`Dynamic scene creation not supported for: '${name}'.`);
      }

      case '': {
        // The empty scene is a placeholder for animation purposes.
        // It should not need to be created.
        throw new Error('Trying to go to empty scene.');
      }

      default: {
        throw assertNever(name);
      }
    }
  }

  // ===================================================================
  // Scene transition animations
  // ===================================================================

  private slideOut(scene: View, duration: number, dir: number) {
    if (!scene) {
      return;
    }

    scene.show();
    const target = uiConfig.width * dir;

    animate(scene).clear().then({ x: target }, duration, animate.easeOut);
  }

  private slideIn(scene: View, duration: number, dir: number) {
    if (!scene) {
      return;
    }

    const from = uiConfig.width * dir;

    scene.updateOpts({ x: from });
    scene.show();

    animate(scene).clear().then({ x: 0 }, duration, animate.easeOut);
  }

  // ===================================================================
  // Scene transition animations
  // ===================================================================

  private fadeIn(scene: View, delay: number, cb: () => void) {
    if (!scene) {
      return;
    }

    scene.show();
    const duration = animDuration;

    animate(scene)
      .clear()
      .wait(delay)
      .then({ opacity: 1, x: 0 }, duration, animate.easeInOut)
      .then(cb);
  }

  private fadeOut(scene: View, cb: () => void) {
    if (!scene) {
      cb();
      return;
    }

    const duration = animDuration * 1;

    animate(scene)
      .clear()

      .then({ opacity: 0, x: 0 }, duration, animate.easeInOut)
      .then(() => {
        scene.hide();
        cb();
      });
  }

  /**
   * Show white screen animation
   */
  public async showWhiteScreenEffect(duration = 3000) {
    const { screenSize } = StateObserver.getState().ui;

    const background = new View({
      tag: 'glowBackground',
      backgroundColor: 'white',
      width: screenSize.width,
      height: screenSize.height,
      y: screenSize.top,
      zIndex: 999,
    });

    const animator = new FloatAnimator((value) => {
      background.style.opacity = value;
    });

    getRootView().addSubview(background);
    await animator.setTarget(1, duration);

    return async () => {
      await animator.setTarget(0, duration);
      background.removeFromSuperview();
    };
  }
}
